import { PluginObject } from 'vue';
import { Store } from 'vuex';

import { AppState } from '@/store';
import { Config } from '@/environment';
import { AppPromiseCacher } from '@/helpers';

import { BagService, AppBagService } from './bag/bag.service';
import { AppEntreeService, EntreeService } from './entree/entree.service';
import { MealService, AppMealService } from './meal/meal.service';
import { MenuService, AppMenuService } from './menu/menu.service';
import { OrderService, AppOrderService } from './order/order.service';
import { CustomerService, AppCustomerService } from './customer/customer.service';
import { UiService, AppUiService } from './ui/ui.service';
import { RestaurantService, AppRestaurantService } from './restaurant/restaurant.service';
import { RecentAndFavoriteOrdersService, AppRecentAndFavoriteOrdersService } from './recent-and-favorite-orders/recent-and-favorite-orders.service';
import { PromotionsService, AppPromotionsService } from './promotions/promotions.service';
import { AppLoyaltyService, LoyaltyService } from './loyalty/loyalty.service';
import { ConfigService, AppConfigService } from './config/config.service';
import { FindAChipotleService, AppFindAChipotleService } from './find-a-chipotle/find-a-chipotle.service';
import { MetadataService, AppMetadataService } from './metadata/metadata.service';
import { AppMealBuilderService, MealBuilderService } from './meal-builder/meal-builder.service';
import { AppSingleEntreeTypeMealBuilderService } from './meal-builder/modes/single-entree/single-entree-meal-builder.service';
import { QuantityItemSelector, SingleItemSelector, AppMealBuilderItemSelector } from './meal-builder/item-selectors';
import { AppZeroVariantMealBuilderService } from './meal-builder/modes/zero-variant/zero-variant.service';
import { TransactionService, AppTransactionService } from './transaction/transaction.service';
import { AppMealBuilderModeMapper } from './meal-builder/modes/meal-builder-mode-mapper.service';
import { Logger, ConsoleLogger } from './logger';
import { AppMultiselectSingleEntreeTypeMealBuilderService } from './meal-builder/modes/multiselect-single-entree/multiselect-single-entree-meal-builder.service';
import { GoogleMapsService, AppGoogleMapsService } from './google/google-maps.service';
import { SputService, AppSputService } from './sput/sput.service';
import { CheckoutService, AppCheckoutService } from './checkout/checkout.service';
import { DeliveryService, AppDeliveryService } from './delivery/delivery.service';
import { AppCheckoutValidationService } from './checkout/checkout-validation.service';
import { RetroCreditService, AppRetroCreditService } from './retro-credit/retro-credit.service';
import { GoogleAnalyticsService, AppGoogleAnalyticsService } from './google/google-analytics.services';
import { EpopService, AppEpopService } from './epop/epop.service';
import { RewardService, AppRewardService } from './reward/reward.service';
import { FeatureFlagsService, AppFeatureFlagsService } from './feature-flags/feature-flags.service';
import { UtilsService, AppUtilsService } from './utils/utils.service';
import VueRouter from 'vue-router';
import { makeApiGatewayHttpClient, makeCmsHttpClient } from './axios-factory';
import { CmsService, AppCmsService } from './cms/cms.service';
import { DonationsService, AppDonationsService } from './donations/donations.service';
import { PaymentService, AppPaymentService } from './payment/payment.service';
import { FeedbackService, AppFeedbackService } from './feedback/feedback.service';
import { SustainabilityService, AppSustainabilityService } from './sustainability/sustainabilty.service';
import { AppMealBuilderAnalyticService, MealBuilderAnalyticService } from './meal-builder/meal-builder-analytics.service';
import { AnalyticsService, AppAnalyticsService } from './analytics/analytics.service';
import { AppBranchService, BranchService } from './branch-service/branch.service';
import { AppMonoMenuService, MonoMenuService } from './mono-menu/mono-menu.service';
import { AppMealBuilderV2Service, MealBuilderV2Service } from './meal-builder-v2/meal-builder-v2.service';
import { AppPepperChatService, PepperChatService } from './pepper-chat/pepper-chat.service';
import { AdobeTargetService, AppAdobeTargetService } from './adobe-target/adobe-target.service';

export interface ServicesContainer {
  bag: BagService;
  checkout: CheckoutService;
  config: ConfigService;
  customer: CustomerService;
  delivery: DeliveryService;
  entree: EntreeService;
  featureFlags: FeatureFlagsService;
  findAChipotle: FindAChipotleService;
  googleMaps: GoogleMapsService;
  analytics: AnalyticsService;
  googleAnalytics: GoogleAnalyticsService;
  mealBuilderAnalytics: MealBuilderAnalyticService;
  logger: Logger;
  loyalty: LoyaltyService;
  meal: MealService;
  mealBuilder: MealBuilderService;
  menu: MenuService;
  metadata: MetadataService;
  order: OrderService;
  pepperChat: PepperChatService;
  promotions: PromotionsService;
  recentAndFavoriteOrders: RecentAndFavoriteOrdersService;
  restaurant: RestaurantService;
  retroCredit: RetroCreditService;
  reward: RewardService;
  payment: PaymentService;
  sput: SputService;
  transaction: TransactionService;
  ui: UiService;
  epop: EpopService;
  utils: UtilsService;
  cms: CmsService;
  donations: DonationsService;
  feedback: FeedbackService;
  sustainability: SustainabilityService;
  branch: BranchService;
  monoMenu: MonoMenuService;
  mealBuilderV2: MealBuilderV2Service;
  adobeTarget: AdobeTargetService;
  loggerFactory: (name: string) => ConsoleLogger;
}

// tslint:disable-next-line: variable-name
export const ServicesPlugin: PluginObject<void> = {
  install: vue => {
    vue.mixin({
      beforeCreate() {
        const options = this.$options;
        if (options.services) {
          const services = options.services;

          this.$services = {
            ...services,
            logger: services.loggerFactory(options.name || 'root'),
          };
        } else if (options.parent && options.parent.$services) {
          const services = options.parent.$services;

          this.$services = {
            ...services,
            // logger: services.loggerFactory(options.name || 'unknown'),
          };
        }
      },
    });
  },
};

export function buildServicesContainer(store: Store<AppState>, config: Config, router: VueRouter): ServicesContainer {
  const rootLogger = new ConsoleLogger('root', config, console);
  const analytics = new AppAnalyticsService(config, store);
  const ui = new AppUiService(store, rootLogger.newInstance('UIService'), analytics);
  const apiGatewayHttpClient = makeApiGatewayHttpClient(config, store, ui, router, rootLogger.newInstance('ApiClient'));

  const utils = new AppUtilsService(store);
  const mealService = new AppMealService();
  const bag = new AppBagService(store, ui);
  const recentAndFavoriteOrders = new AppRecentAndFavoriteOrdersService(
    store,
    apiGatewayHttpClient,
    rootLogger.newInstance('AppRecentAndFavoriteOrdersService'),
    new AppPromiseCacher(rootLogger.newInstance('AppCustomerServicePromiseCacher')),
    analytics,
  );
  const configService = new AppConfigService(config, store);
  const findAChipotle = new AppFindAChipotleService(store, apiGatewayHttpClient, rootLogger, analytics);
  const googleMaps = new AppGoogleMapsService(config, store, apiGatewayHttpClient, rootLogger.newInstance('AppGoogleMapsService'), analytics);

  const transaction = new AppTransactionService(
    config,
    new AppPromiseCacher(rootLogger.newInstance('AppTransactionServicePromiseCacher')),
    store,
    apiGatewayHttpClient,
    rootLogger.newInstance('AppTransactionService'),
    analytics,
  );

  const promotions = new AppPromotionsService(
    store,
    apiGatewayHttpClient,
    rootLogger.newInstance('AppPromotionsService'),
    new AppPromiseCacher(rootLogger.newInstance('AppPromotionsServicePromiseCacher')),
    analytics,
  );

  const metadata = new AppMetadataService(
    store,
    apiGatewayHttpClient,
    rootLogger.newInstance('AppMetadataService'),
    new AppPromiseCacher(rootLogger.newInstance('AppMetadataServicePromiseCacher')),
    analytics,
  );

  const googleAnalytics = new AppGoogleAnalyticsService(config, metadata, store, rootLogger.newInstance('AppGoogleAnalyticsService'));

  const mealBuilderAnalytics = new AppMealBuilderAnalyticService(googleAnalytics);
  const payment = new AppPaymentService(config, store, apiGatewayHttpClient, rootLogger.newInstance('AppPaymentService'), analytics);
  const order = new AppOrderService(payment, promotions, ui, bag, googleAnalytics, analytics, apiGatewayHttpClient, store, rootLogger.newInstance('AppOrderService'));
  const menu = new AppMenuService(order, store, apiGatewayHttpClient, rootLogger.newInstance('AppMenuService'), {
    channel: config.services.channel,
  }, analytics);
  const entreeService = new AppEntreeService(rootLogger.newInstance('AppEntreeService'), utils, menu);

  const restaurant = new AppRestaurantService(order, googleAnalytics, ui, configService, store, apiGatewayHttpClient, rootLogger.newInstance('AppRestaurantService'), analytics);
  const featureFlags = new AppFeatureFlagsService(store, apiGatewayHttpClient, rootLogger.newInstance('AppFeatureFlagsService'), analytics);

  const customer = new AppCustomerService(
    store,
    apiGatewayHttpClient,
    restaurant,
    order,
    new AppPromiseCacher(rootLogger.newInstance('AppCustomerServicePromiseCacher')),
    rootLogger.newInstance('AppCustomerService'),
    analytics,
    ui
  );

  const loyalty = new AppLoyaltyService(
    customer,
    store,
    apiGatewayHttpClient,
    order,
    rootLogger.newInstance('LoyaltyService'),
    new AppPromiseCacher(rootLogger.newInstance('AppTransactionServicePromiseCacher')),
    googleAnalytics,
    featureFlags,
    configService,
    analytics
  );

  const pepperChat = new AppPepperChatService(store, loyalty);
  const sput = new AppSputService(store, apiGatewayHttpClient, rootLogger.newInstance('AppSputService'), analytics);
  const reward = new AppRewardService(store, apiGatewayHttpClient, rootLogger.newInstance('AppRewardService'), featureFlags, analytics);
  const mealBuilder = makeMealBuilderService(utils, menu, store, metadata, order, rootLogger, mealBuilderAnalytics, analytics);
  const delivery = new AppDeliveryService(store, apiGatewayHttpClient, rootLogger.newInstance('AppDeliveryService'), order, analytics);
  const checkout = new AppCheckoutService(store, new AppCheckoutValidationService(store, order), delivery, order, rootLogger.newInstance('AppCheckoutService'));
  const retroCredit = new AppRetroCreditService(store, apiGatewayHttpClient, rootLogger.newInstance('AppRetroCreditService'), analytics);
  const epop = new AppEpopService(store, apiGatewayHttpClient, rootLogger.newInstance('AppEpopService'), analytics);
  const cms = new AppCmsService(restaurant, makeCmsHttpClient(config), store, rootLogger.newInstance('AppCmsService'), analytics);
  const donations = new AppDonationsService(store, apiGatewayHttpClient, rootLogger.newInstance('AppDonationsService'), analytics);
  const feedback = new AppFeedbackService(store, apiGatewayHttpClient, rootLogger.newInstance('AppFeedbackService'), analytics);
  const sustainability = new AppSustainabilityService(store, apiGatewayHttpClient, rootLogger.newInstance('AppSustainabilityService'), analytics);
  const branch = new AppBranchService(store, rootLogger.newInstance('BranchService'));
  const monoMenu = new AppMonoMenuService(
    store,
    menu,
    metadata,
    loyalty,
    rootLogger.newInstance('MonoMenuService'),
    new AppPromiseCacher(rootLogger.newInstance('AppMonoMenuServicePromiseCacher')),
  );
  const mealBuilderV2 = new AppMealBuilderV2Service(
    store,
    monoMenu,
    ui,
    order,
    customer,
    menu,
    router,
    rootLogger.newInstance('MonoMenuService'),
    new AppPromiseCacher(rootLogger.newInstance('AppMealBuilderV2ServicePromiseCacher')),
  );
  const adobeTarget = new AppAdobeTargetService(
    config,
    store,
    rootLogger.newInstance('AdobeTargetService'),
    analytics,
    metadata,
    configService,
    order,
  );

  return {
    bag,
    checkout,
    config: configService,
    customer,
    delivery,
    entree: entreeService,
    featureFlags,
    findAChipotle,
    googleMaps,
    googleAnalytics,
    analytics,
    mealBuilderAnalytics,
    logger: rootLogger,
    loyalty,
    meal: mealService,
    mealBuilder,
    menu,
    metadata,
    order,
    pepperChat,
    promotions,
    recentAndFavoriteOrders,
    restaurant,
    retroCredit,
    reward,
    payment,
    sput,
    transaction,
    ui,
    epop,
    cms,
    donations,
    utils,
    feedback,
    loggerFactory: (name) => rootLogger.newInstance(name),
    sustainability,
    branch,
    monoMenu,
    mealBuilderV2,
    adobeTarget
  };
}

export function makeMealBuilderService(
  utils: UtilsService,
  menu: MenuService,
  store: Store<AppState>,
  metadata: MetadataService,
  order: OrderService,
  rootLogger: Logger,
  mealBuilderAnalytics: AppMealBuilderAnalyticService,
  analytics: AnalyticsService,
): MealBuilderService {
  const quantityItemSelector = new QuantityItemSelector(rootLogger.newInstance('QuantityItemSelector'));
  const singleItemSelector = new SingleItemSelector(rootLogger.newInstance('SingleItemSelector'));
  const mealBuilderItemSelector = new AppMealBuilderItemSelector(
    store,
    quantityItemSelector,
    singleItemSelector,
    rootLogger.newInstance('AppMealBuilderItemSelector'),
    mealBuilderAnalytics,
    analytics,
  );

  const singleEntreeService = new AppSingleEntreeTypeMealBuilderService(
    utils,
    menu,
    store,
    mealBuilderItemSelector,
    rootLogger.newInstance('AppSingleEntreeTypeMealBuilderService'),
    mealBuilderAnalytics,
    analytics,
  );
  const multiselectSingleEntreeService = new AppMultiselectSingleEntreeTypeMealBuilderService(
    utils,
    menu,
    store,
    mealBuilderItemSelector,
    rootLogger.newInstance('AppMultiselectSingleEntreeTypeMealBuilderService'),
    mealBuilderAnalytics,
    analytics,
  );
  const zeroVariantService = new AppZeroVariantMealBuilderService(
    utils,
    menu,
    store,
    mealBuilderItemSelector,
    rootLogger.newInstance('AppZeroVariantMealBuilderService'),
    mealBuilderAnalytics,
    analytics,
  );

  const mealBuilderModeMapper = new AppMealBuilderModeMapper(metadata, utils, menu, rootLogger.newInstance('AppMealBuilderModeMapper'));

  const mealBuilder = new AppMealBuilderService(
    utils,
    menu,
    store,
    singleEntreeService,
    multiselectSingleEntreeService,
    zeroVariantService,
    mealBuilderModeMapper,
    mealBuilderItemSelector,
    rootLogger.newInstance('AppMealBuilderService'),
    mealBuilderAnalytics,
    analytics,
  );

  return mealBuilder;
}
